import { api } from '@data/store';
import {
  ClientAccessRole,
  ClientCompany,
  ClientCompanyPerson,
  ClientCompanyProduct,
  ClientCompanySummary,
  ListResponse,
} from '@data/utils/types';

export const fetchClientCompanies = (organizationId: string): Promise<ListResponse<ClientCompany>> =>
  api.get(`/organizations/${organizationId}/client-companies`);

export const fetchClientCompany = (organizationId: string, clientId: string): Promise<ClientCompany> =>
  api.get(`/organizations/${organizationId}/client-companies/${clientId}`);

/*
 * A patch: either field may be left out, so renaming does not clear the product list.
 *
 * `silentStatuses` on every write below that has something of its own to say. A 400 here is a rule the
 * screen already explains — a name another client holds, a product that is not theirs, the last admin —
 * and without this the global error toast fires alongside that message, so a reader gets the same
 * refusal twice in two different wordings.
 */
/** What a client's page may change about it. Each field is sent only when it actually changed. */
export interface ClientCompanyUpdate {
  name?: string;
  description?: string;
  /** Null clears it. Absent means the folder is not what is being changed. */
  legalFolderUrl?: string | null;
  msaUrl?: string | null;
  sowUrl?: string | null;
  slaUrl?: string | null;
  dpaUrl?: string | null;
  ndaUrl?: string | null;
  companyRegisterUrl?: string | null;
  otherRequirements?: string;
  /** Null clears the date. The label is cleared with an empty string, as the description is. */
  importantDate?: string | null;
  importantDateLabel?: string;
  /** An instant: when somebody confirmed the record is correct. Null clears it, and the reviewer with it. */
  lastReviewedAt?: string | null;
  productIds?: string[];
}

export const updateClientCompany = (
  organizationId: string,
  clientId: string,
  values: ClientCompanyUpdate,
): Promise<void> =>
  api.patch(`/organizations/${organizationId}/client-companies/${clientId}`, values, { silentStatuses: [400] });

export const createClientCompany = (
  organizationId: string,
  values: { name: string; productIds?: string[] },
): Promise<ClientCompany> =>
  api.post(`/organizations/${organizationId}/client-companies`, values, { silentStatuses: [400] });

export const deleteClientCompany = (organizationId: string, clientId: string): Promise<void> =>
  api.delete(`/organizations/${organizationId}/client-companies/${clientId}`);

export const addClientCompanyPerson = (
  organizationId: string,
  clientId: string,
  values: { firstName: string; lastName?: string; email: string; productIds?: string[] },
): Promise<ClientCompanyPerson> =>
  api.post(`/organizations/${organizationId}/client-companies/${clientId}/people`, values, {
    silentStatuses: [400],
  });

export const updateClientCompanyPerson = (
  organizationId: string,
  clientId: string,
  membershipId: string,
  values: { role?: ClientAccessRole; productIds?: string[] },
): Promise<ClientCompanyPerson> =>
  api.patch(`/organizations/${organizationId}/client-companies/${clientId}/people/${membershipId}`, values, {
    silentStatuses: [400, 403],
  });

export const removeClientCompanyPerson = (
  organizationId: string,
  clientId: string,
  membershipId: string,
): Promise<void> => api.delete(`/organizations/${organizationId}/client-companies/${clientId}/people/${membershipId}`);

/**
 * Sends the person the link that lets them choose a password and sign in.
 *
 * `silentStatuses` covers the refusals this screen words itself — mail switched off for the
 * environment, or a deactivated account — so the raw sentence never reaches a toast.
 */
export const sendClientSetPasswordEmail = (
  organizationId: string,
  clientId: string,
  membershipId: string,
): Promise<void> =>
  api.post(
    `/organizations/${organizationId}/client-companies/${clientId}/people/${membershipId}/set-password-email`,
    {},
    { silentStatuses: [400] },
  );

/*
 * The client's own view of their end of the relationship: who else reads the reports, and inviting
 * one more. Scoped by the API to the caller's own client company.
 */
export const fetchMyClientCompanies = (organizationId: string): Promise<ListResponse<ClientCompanySummary>> =>
  api.get(`/organizations/${organizationId}/client/companies`);

export const fetchClientColleagues = (
  organizationId: string,
  clientCompanyId?: string,
): Promise<ListResponse<ClientCompanyPerson>> =>
  api.get(`/organizations/${organizationId}/client/colleagues`, { params: { clientCompanyId } });

export const updateClientAccess = (
  organizationId: string,
  clientCompanyId: string,
  membershipId: string,
  values: { role?: ClientAccessRole; productIds?: string[] },
): Promise<ClientCompanyPerson> =>
  api.patch(`/organizations/${organizationId}/client/companies/${clientCompanyId}/people/${membershipId}`, values, {
    silentStatuses: [400, 403],
  });

export const removeClientAccess = (
  organizationId: string,
  clientCompanyId: string,
  membershipId: string,
): Promise<void> =>
  api.delete(`/organizations/${organizationId}/client/companies/${clientCompanyId}/people/${membershipId}`, {
    silentStatuses: [400, 403],
  });

export const inviteClientColleague = (
  organizationId: string,
  values: {
    firstName: string;
    lastName?: string;
    email: string;
    productIds?: string[];
    role?: ClientAccessRole;
    clientCompanyId?: string;
  },
): Promise<ClientCompanyPerson> =>
  api.post(`/organizations/${organizationId}/client/colleagues`, values, { silentStatuses: [400, 403] });

/** The products this client may read, which is also the most an invitation of theirs can carry. */
export const fetchClientProducts = (organizationId: string): Promise<ListResponse<ClientCompanyProduct>> =>
  api.get(`/organizations/${organizationId}/client/products`);
